import { useEffect, useState } from 'react';
import {
  Button, Dropdown, Field, Input, MessageBar, MessageBarBody, Option, Text, makeStyles, tokens,
} from '@fluentui/react-components';
import { api } from '../../api/client';
import type { AzurePack, AzureSubscription, AzureTargets, DeploymentSession } from '../../api/types';
import { connectAzure, customerTokens, type TokenOptions } from '../../customer/customerAuth';

const useStyles = makeStyles({
  stack: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  row: { display: 'flex', gap: tokens.spacingHorizontalM, alignItems: 'end', flexWrap: 'wrap' },
  mono: { fontFamily: tokens.fontFamilyMonospace, fontSize: tokens.fontSizeBase200 },
});

/** How often the page re-enters the azure step while Azure is deploying. */
const POLL_MS = 15_000;

/** Same link the API builds (azurePack.portalDeployUrl). */
export function portalDeployUrl(pack: AzurePack): string {
  const base = `https://portal.azure.com/#create/Microsoft.Template/uri/${encodeURIComponent(pack.templateUri)}`;
  return pack.createUiDefinitionUri ? `${base}/createUIDefinitionUri/${encodeURIComponent(pack.createUiDefinitionUri)}` : base;
}

interface Props {
  session: DeploymentSession;
  pack: AzurePack;
  busy: boolean;
  /** Runs the azure step with this input and these customer tokens. */
  onRun: (input: Record<string, unknown>, tokens: TokenOptions) => void;
}

/**
 * The azure step for a product with an Azure pack.
 *  Option A: the admin consents to Azure here (incremental — only now, only for this product), the
 *            engineer picks subscription / resource group / region, and DM deploys; the page then
 *            checks progress every 15 s.
 *  Option B: "Deploy to Azure" in the portal, then the deployment's outputs typed in here. Used when
 *            Azure consent is refused or Azure says no.
 */
export function AzurePackPanel({ session, pack, busy, onRun }: Props) {
  const styles = useStyles();
  const run = session.azure;
  const mode = run?.mode ?? 'arm';
  const step = session.steps.azure;
  const appReg = !!pack.appRegistration;
  const armTokens: TokenOptions = { azure: true, appRegistration: appReg };

  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subs, setSubs] = useState<AzureSubscription[] | null>(null);
  const [sub, setSub] = useState('');
  const [targets, setTargets] = useState<AzureTargets | null>(null);
  const [rg, setRg] = useState('');
  const [location, setLocation] = useState('');
  const [outputs, setOutputs] = useState<Record<string, string>>({});

  const submitted = mode === 'arm' && !!run?.deploymentName && run.provisioningState !== 'Failed';

  // While Azure deploys, re-enter the step on a timer (silent tokens only — no popups in the background).
  useEffect(() => {
    if (!(submitted && step.status === 'running') || busy) return undefined;
    const t = setTimeout(() => onRun({}, { ...armTokens, silentOnly: true }), POLL_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitted, step.status, busy, session.updatedAt]);

  // Deploy to Azure still needs DM to create the API app registration: the admin is asked for that
  // consent (only) here. Refusing it stops the step — there is no typed-in alternative.
  // Record that consent is being asked for BEFORE the popup, so every sweep knows to take it back.
  const ledger = () => api(`sessions/${session.id}/azure/consent-requested`, { body: {} });
  const usePortal = async (reason: string) => {
    try { await ledger(); } catch (e) { setError((e as Error).message); return; }
    onRun({ mode: 'portal', reason }, { appRegistration: appReg });
  };
  const portalParams = (step.detail?.portalParameters ?? {}) as Record<string, string | number | boolean>;

  async function signInToAzure() {
    setError(null);
    try {
      await ledger();
      await connectAzure(appReg);
      setConnected(true);
      setSubs(await api<AzureSubscription[]>(`sessions/${session.id}/azure/subscriptions`, { customer: await customerTokens({ azure: true }) }));
    } catch (e) {
      setError((e as Error).message || 'Azure sign-in did not complete.');
    }
  }

  async function chooseSub(id: string) {
    setSub(id); setTargets(null); setRg(''); setLocation('');
    try {
      setTargets(await api<AzureTargets>(`sessions/${session.id}/azure/subscriptions/${id}/targets`, { customer: await customerTokens({ azure: true }) }));
    } catch (e) {
      setError((e as Error).message);
    }
  }

  const existingRg = targets?.resourceGroups.find((g) => g.name.toLowerCase() === rg.trim().toLowerCase());
  const region = existingRg?.location ?? location;

  // ---------------- option B
  if (mode === 'portal') {
    const missing = pack.outputs.filter((o) => !outputs[o]?.trim());
    return (
      <div className={styles.stack}>
        <MessageBar intent="warning"><MessageBarBody>
          The customer deploys the backend from the Azure portal. {run?.fallbackReason && <>Why: {run.fallbackReason}</>}
        </MessageBarBody></MessageBar>
        <Text>1. The customer&apos;s admin opens the template in the portal, fills in the form and creates the deployment.</Text>
        <div className={styles.row}>
          <Button as="a" appearance="primary" href={portalDeployUrl(pack)} target="_blank" rel="noreferrer">Deploy to Azure</Button>
        </div>
        {run?.app && (
          <Text className={styles.mono}>API app registration Deployment Manager created: client id {run.app.clientId} · audience {run.app.audience}</Text>
        )}
        {Object.keys(portalParams).length > 0 && (
          <div className={styles.stack}>
            <Text>Values for the portal form (the rest, such as the region, are the admin&apos;s choice):</Text>
            {Object.entries(portalParams).map(([k, v]) => <Text key={k} className={styles.mono}>{k}: {String(v)}</Text>)}
          </div>
        )}
        <Text>2. When it has finished, copy these values from the deployment&apos;s Outputs page:</Text>
        {pack.outputs.map((o) => (
          <Field key={o} label={o} required>
            <Input value={outputs[o] ?? ''} onChange={(_, d) => setOutputs({ ...outputs, [o]: d.value })} />
          </Field>
        ))}
        <div className={styles.row}>
          <Button appearance="primary" disabled={busy || missing.length > 0} onClick={() => onRun({ mode: 'portal', outputs }, {})}>
            Check the backend
          </Button>
        </div>
      </div>
    );
  }

  // ---------------- option A, deployment in progress (or its result)
  if (run?.deploymentName && run.provisioningState !== 'Failed') {
    return (
      <div className={styles.stack}>
        <Text>
          Deployment <span className={styles.mono}>{run.deploymentName}</span> in {run.resourceGroup} ({run.location}):{' '}
          <b>{run.provisioningState ?? 'submitted'}</b>. {step.status === 'running' && 'Checked every 15 seconds; this usually takes 10–20 minutes.'}
        </Text>
        {!connected && step.status === 'running' && (
          <Text size={200}>If checking stops with a sign-in error, sign in to Azure again: <Button size="small" onClick={signInToAzure}>Sign in to Azure</Button></Text>
        )}
      </div>
    );
  }

  // ---------------- option A, choose where to deploy
  return (
    <div className={styles.stack}>
      <Text>
        Deployment Manager can deploy {session.displayName}&apos;s Azure backend into the customer&apos;s subscription. The customer&apos;s
        admin signs in to Azure once{appReg ? ' and allows Deployment Manager to create the API app registration' : ''}.
      </Text>
      {!connected && (
        <div className={styles.row}>
          <Button appearance="primary" disabled={busy} onClick={signInToAzure}>Sign in to Azure</Button>
          <Button disabled={busy} onClick={() => usePortal('The engineer chose Deploy to Azure.')}>Use Deploy to Azure instead</Button>
        </div>
      )}
      {error && (
        <MessageBar intent="error"><MessageBarBody>
          {error}{' '}
          <Button size="small" onClick={() => usePortal(`Azure consent or access was not available: ${error}`.slice(0, 280))}>Use Deploy to Azure instead</Button>
        </MessageBarBody></MessageBar>
      )}
      {connected && subs && (
        <div className={styles.row}>
          <Field label="Subscription" required>
            <Dropdown placeholder={subs.length ? 'Choose a subscription' : 'No subscriptions this admin can use'}
                      value={subs.find((x) => x.subscriptionId === sub)?.displayName ?? ''}
                      onOptionSelect={(_, d) => d.optionValue && chooseSub(d.optionValue)}>
              {subs.map((x) => <Option key={x.subscriptionId} value={x.subscriptionId} text={x.displayName}>{`${x.displayName} (${x.subscriptionId})`}</Option>)}
            </Dropdown>
          </Field>
          {targets && (
            <>
              <Field label="Resource group (existing, or a new name)" required>
                <Input list="dm-azure-rgs" value={rg} onChange={(_, d) => setRg(d.value)} placeholder="rg-projectiq" />
                <datalist id="dm-azure-rgs">{targets.resourceGroups.map((g) => <option key={g.name} value={g.name} />)}</datalist>
              </Field>
              <Field label="Region" hint={existingRg ? `Uses the resource group's region (${existingRg.location}).` : undefined} required>
                <Dropdown disabled={!!existingRg} placeholder="Choose a region"
                          value={targets.locations.find((l) => l.name === region)?.displayName ?? region}
                          onOptionSelect={(_, d) => setLocation(d.optionValue ?? '')}>
                  {targets.locations.map((l) => <Option key={l.name} value={l.name} text={l.displayName}>{l.displayName}</Option>)}
                </Dropdown>
              </Field>
              <Button appearance="primary" disabled={busy || !sub || !rg.trim() || !region}
                      onClick={() => onRun({ subscriptionId: sub, resourceGroup: rg.trim(), location: region }, armTokens)}>
                Deploy to the customer&apos;s Azure
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
